import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdLink,
  MdLinkOff,
  MdTitle,
  MdFormatClear,
  MdCode,
  MdOutlineVisibility,
  MdOutlineWarningAmber,
} from "react-icons/md";

/*
  Editor do corpo do e-mail, com dois modos sobre o MESMO texto.

  - **Visual** — quem não escreve HTML formata com os botões.
  - **HTML** — quem escreve (ou cola um e-mail pronto de outra ferramenta) mexe
    no código direto.

  Os dois editam a mesma string: trocar de aba não converte nada, só muda como
  ela é exibida. É o que permite começar no visual, abrir o HTML para ajustar
  um detalhe e voltar.

  **Sobre `document.execCommand`:** está marcado como obsoleto na
  especificação, e mesmo assim é o que todo navegador atual implementa para
  `contentEditable`. A alternativa seria trazer um editor de terceiros
  (Quill, TipTap) — centenas de KB e um modelo de documento próprio — para
  formatar negrito e lista num corpo de e-mail, que é HTML dos anos 2000 por
  obrigação dos clientes de e-mail. Não compensa.
*/

const BOTOES = [
  { cmd: "bold", icon: MdFormatBold, title: "Negrito" },
  { cmd: "italic", icon: MdFormatItalic, title: "Itálico" },
  { cmd: "formatBlock", valor: "<h2>", icon: MdTitle, title: "Título" },
  { cmd: "insertUnorderedList", icon: MdFormatListBulleted, title: "Lista" },
];

const HtmlEditor = forwardRef(
  ({ value, onChange, disabled = false, placeholder }, ref) => {
  const [modo, setModo] = useState("visual");
  const areaRef = useRef(null);
  const ultimoValorEmitido = useRef(value);

  /*
    Só reescreve o DOM quando o valor vem de FORA (troca de aba, carregamento
    da campanha). Escrever a cada tecla recriaria os nós do contentEditable e
    jogaria o cursor para o início a cada letra digitada — é o erro clássico
    de controlar contentEditable com React.
  */
  useEffect(() => {
    if (modo !== "visual" || !areaRef.current) return;
    if (value === ultimoValorEmitido.current) return;

    areaRef.current.innerHTML = value || "";
    ultimoValorEmitido.current = value;
  }, [value, modo]);

  // Ao voltar para o visual, o DOM precisa receber o que foi editado no HTML.
  useEffect(() => {
    if (modo === "visual" && areaRef.current) {
      areaRef.current.innerHTML = value || "";
      ultimoValorEmitido.current = value;
    }
  }, [modo]); // eslint-disable-line react-hooks/exhaustive-deps

  const emitir = () => {
    const html = areaRef.current?.innerHTML || "";
    ultimoValorEmitido.current = html;
    onChange(html);
  };

  const comando = (cmd, valor) => {
    if (disabled) return;
    areaRef.current?.focus();
    document.execCommand(cmd, false, valor);
    emitir();
  };

  const inserirLink = () => {
    const url = window.prompt("Endereço do link (começando com https://)");
    if (!url) return;

    if (!/^https?:\/\//i.test(url)) {
      window.alert(
        "O link precisa começar com http:// ou https:// — outros esquemas não são clicáveis no e-mail."
      );
      return;
    }

    comando("createLink", url);
  };

  /*
    Inserir variável NO CURSOR, não no fim do texto.

    Com `{{nome}}` grudando sempre no final, escrever "Oi {{nome}}, tudo bem?"
    virava: digitar tudo, clicar no chip, recortar do fim e colar no meio. O
    chip é o atalho mais usado da tela — tinha que cair onde o cursor está.
  */
  useImperativeHandle(ref, () => ({
    inserirVariavel: (chave) => {
      const marcador = `{{${chave}}}`;

      if (modo === "html") {
        onChange(`${value || ""}${marcador}`);
        return;
      }

      areaRef.current?.focus();
      document.execCommand("insertText", false, marcador);
      emitir();
    },
  }));

  const documentoCompleto =
    /^\s*<!doctype/i.test(value || "") || /<html[\s>]/i.test(value || "");

  return (
    <div className="rounded-xl border border-slate-200 focus-within:border-pink-300 focus-within:ring-2 focus-within:ring-pink-100">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-100 px-2 py-1.5">
        {modo === "visual" ? (
          <>
            {BOTOES.map((botao) => (
              <button
                key={botao.cmd + (botao.valor || "")}
                type="button"
                title={botao.title}
                disabled={disabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => comando(botao.cmd, botao.valor)}
                className="rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"
              >
                <botao.icon size={17} />
              </button>
            ))}
            <button
              type="button"
              title="Inserir link"
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={inserirLink}
              className="rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"
            >
              <MdLink size={17} />
            </button>
            <button
              type="button"
              title="Remover link"
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => comando("unlink")}
              className="rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"
            >
              <MdLinkOff size={17} />
            </button>
            <button
              type="button"
              title="Limpar formatação"
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => comando("removeFormat")}
              className="rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40"
            >
              <MdFormatClear size={17} />
            </button>
          </>
        ) : (
          <span className="px-1 text-[11px] text-slate-500">
            Cole aqui um e-mail pronto ou ajuste o código à mão
          </span>
        )}

        <div className="ml-auto flex gap-1">
          {["visual", "html"].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setModo(item)}
              className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                modo === item
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {item === "visual" ? (
                <>
                  <MdOutlineVisibility size={13} /> Visual
                </>
              ) : (
                <>
                  <MdCode size={13} /> HTML
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {modo === "visual" ? (
        <div
          ref={areaRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emitir}
          onBlur={emitir}
          data-placeholder={placeholder}
          className="min-h-[260px] w-full overflow-auto p-3 text-sm leading-relaxed text-slate-800 outline-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)] [&_a]:text-pink-700 [&_a]:underline [&_h2]:mb-1 [&_h2]:mt-2 [&_h2]:text-base [&_h2]:font-bold [&_li]:ml-4 [&_li]:list-disc [&_p]:mb-2"
        />
      ) : (
        <textarea
          value={value}
          disabled={disabled}
          onChange={(event) => {
            ultimoValorEmitido.current = event.target.value;
            onChange(event.target.value);
          }}
          rows={14}
          placeholder={placeholder}
          className="w-full resize-y rounded-b-xl p-3 font-mono text-[13px] leading-relaxed outline-none"
        />
      )}

      {documentoCompleto ? (
        /*
          Aviso, não bloqueio. Um documento completo é legítimo — alguém colou
          um e-mail desenhado em outra ferramenta — mas muda o que acontece no
          envio: o layout padrão da Relinq não é aplicado. Sem o aviso, a
          pessoa só descobre isso ao ver a prévia e acha que quebrou.
        */
        <p className="flex items-start gap-1.5 border-t border-slate-100 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-800">
          <MdOutlineWarningAmber size={14} className="mt-px shrink-0" />
          <span>
            Isto é um documento HTML completo — vai sair exatamente como está,
            sem o cabeçalho e o visual padrão da Relinq. O rodapé com o link de
            descadastro continua sendo acrescentado, porque é obrigatório.
          </span>
        </p>
      ) : null}
    </div>
  );
  }
);

HtmlEditor.displayName = "HtmlEditor";

export default HtmlEditor;
